import { ModelBase, UserRole } from './base';
import { Address } from './address';

export enum AgencyStatus {
  Active = 'active',
  Disabled = 'disabled',
}

/** What `Agency.signupBonusTokens` means when the agency never set it. */
export const DEFAULT_AGENCY_SIGNUP_BONUS_TOKENS = 500;
/** The most an agency may set as its signup bonus, and grant a creator at once. */
export const MAX_AGENCY_SIGNUP_BONUS_TOKENS = 10000;

// /agencies/{auto-id}
export interface Agency extends ModelBase {
  logo?: string;
  // Credits granted ONCE to each creator who signs up through this agency's
  // link. Those creators are sponsored (the agency pays, so they never see the
  // plan picker) but, unlike an admin-sponsored creator, are never topped up
  // automatically. Set in the agency portal's Settings; absent means
  // `DEFAULT_AGENCY_SIGNUP_BONUS_TOKENS`, 0 means no bonus.
  signupBonusTokens?: number;
  // How many creators may be tagged to this agency (signup link or admin
  // assignment). Set by POP.STORE staff, 1–4999; ABSENT means unlimited, which
  // is every agency created before seats existed. When `creatorSeatsUsed`
  // reaches it, signup through the agency's link is refused.
  creatorSeatLimit?: number;
  // Creators currently tagged to this agency. A counter moved only inside a
  // transaction that also reads the agency (account create, `assignAgency`);
  // absent means never counted, and the first writer initialises it from a
  // count of `stores` carrying this `agencyId`.
  creatorSeatsUsed?: number;
  name: string;
  email: string;
  address: Address;
  status: AgencyStatus;
  // The stable identifier a signup URL carries as `?utm_agency=<slug>`. Lowercase
  // `[a-z0-9-]`, unique across agencies, and IMMUTABLE once set: `name` is free
  // text an operator renames, so resolving a campaign link by name would break
  // every live URL the moment somebody fixed a typo.
  //
  // Optional because every agency predating this field has none; a creator
  // arriving with an unresolvable value is left untagged rather than guessed at.
  slug?: string;
  // DEPRECATED as a gate, and no longer read by `decideAgencyTag`.
  //
  // It was an admin opt-in: an agency became reachable by a public `utm_agency`
  // link only once somebody enrolled it here. Every agency is UTM-tagged now, so
  // the flag would be true for every row — a gate that is always open defends
  // nothing, while leaving every agency predating it silently untaggable.
  //
  // Still declared because rows carry it and the Elasticsearch mapping indexes
  // it; drop the field, the mapping and the writes together or not at all.
  //
  // What stops a guessed slug reaching a real agency is now the slug itself —
  // operator-set, immutable once set, resolving to exactly one agency — plus
  // first-write-wins and the audit row written on every outcome.
  utmTaggingEnabled?: boolean;
}

/** Why a creator stopped belonging to an agency. */
export type AgencyFormerCreatorReason = 'deleted' | 'untagged';

// /agencies/{agencyId}/formerCreators/{sellerId}
//
// A creator who USED TO belong to this agency: they deleted their POP.STORE
// account, or were untagged / moved to another agency. Written in the same
// transaction that clears the tag and frees the seat (`moveCreatorAgency`), so
// the portal can list them with a badge while counting them nowhere. Removed
// again if the creator is tagged back to this agency.
export interface AgencyFormerCreator {
  sellerId: string;
  /** Their name when they left — a deleted account's live name is masked. */
  name: string;
  reason: AgencyFormerCreatorReason;
  removedAt: number;
  /** Who removed them from the portal, as `portal:<email>`. */
  removedBy?: string;
}

export type AgencyCreatorInviteStatus = 'pending' | 'accepted' | 'revoked';

// /agencies/{agencyId}/creatorInvites/{auto-id}
//
// A creator the agency emailed an invite to. The email carries the agency's
// ordinary signup link (`/signup?utm_agency=<slug>`), so an invite is not a
// credential: it records who was asked, and it is ACCEPTED when someone signs
// up with that email and is tagged to this agency (the signup transaction
// does that). It never reserves a seat.
export interface AgencyCreatorInvite {
  /** Lowercased and trimmed; what the signup is matched on. */
  email: string;
  status: AgencyCreatorInviteStatus;
  /** Portal user who sent it, as `portal:<email>`. */
  invitedBy: string;
  invitedAt: number;
  lastSentAt: number;
  sendCount: number;
  acceptedSellerId?: string;
  acceptedAt?: number;
  revokedAt?: number;
}

// /agencies/{auto-id}/users/{auto-id}
export interface AgencyUser extends ModelBase {
  avatar?: string;
  name: string;
  email: string;
  role: UserRole.AgencyAdmin | UserRole.AgencyUser;
  isDefault: boolean;
  disabled: boolean;
}

// /agencies/{auto-id}/impersonations/{auto-id}
// One record per time an agency admin signed in as a store tagged to that
// agency. Written by the backend when it mints the login token, never by a
// client — it is the only trace of who acted as whom.
export interface AgencyImpersonation extends ModelBase {
  agencyUserId: string;
  agencyUserEmail: string;
  sellerId: string;
}
